import {useState, useEffect, useRef, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import usePageTitle from '../../../hooks/usePageTitle';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClipboardIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';
import {getLocalTimezoneLabel} from '../../../utils/datetime';
import FormInput from '../../../components/FormInput';
import {useBreadcrumbs} from '../../../contexts/BreadcrumbContext';
import {toast} from 'react-toastify';
import {
  sendInvite,
  clearUserMessages,
  fetchInvites,
  selectInvites,
  selectUserLoading,
  selectUserError,
  selectUserSuccess,
  fetchManagedResources,
  selectManagedResources,
  selectManagedResourcesLoading,
} from '../../../slices/userSlice';
import {selectAuthIsAdmin, selectAuthIsMarketOwnerReadOnly} from '../../../slices/authSlice';
import {selectBlurSensitive} from '../../../slices/preferencesSlice';
import CustomButton from '../../../components/CustomButton';
import Pagination from '../../../components/Pagination';

export default function InviteUser() {
  usePageTitle('Invite user');
  const dispatch = useDispatch();
  const loading = useSelector(selectUserLoading);
  const errorMessage = useSelector(selectUserError);
  const successMessage = useSelector(selectUserSuccess);
  const invitesRaw = useSelector(selectInvites);
  const invites = useMemo(() => invitesRaw || [], [invitesRaw]);
  const blurSensitive = useSelector(selectBlurSensitive);
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  useBreadcrumbs(
    isAdmin
      ? [{ label: 'Users', href: '/users/list-users' }, { label: 'Invite' }]
      : [{ label: 'Invite' }],
    'Send an invitation to a new forecaster'
  );

  // Role state for the invite (default: forecaster)
  const [role, setRole] = useState('Forecaster');

  // Trial forecasters can only access the playground instance until promoted.
  // Default on: new forecasters are evaluated in the playground first.
  const [isTrial, setIsTrial] = useState(true);

  // Resource management - use Redux store
  const availableResources = useSelector(selectManagedResources);
  const loadingResources = useSelector(selectManagedResourcesLoading);
  const [selectedResources, setSelectedResources] = useState([]);

  // Role descriptions shown in helper text
  // Use ROLE_CHOICES with lowercase values as requested by backend
  const ROLE_CHOICES = [
    ['Forecaster', 'Forecaster'],
    ['Market Owner Viewer', 'Market Owner Read Only'],
    ['Market Owner', 'Market Owner'],
  ];

  const roleDescriptions = {
    forecaster: 'Can submit and edit forecasts',
    admin: 'Full access, including user and settings management',
    market_owner: 'Owner of a market: full access to market data and forecasts',
    market_owner_read: 'Market owner with read-only access to market data',
  };

  const roleOptions = ROLE_CHOICES.map(([value, label]) => ({value, label}));

  const roleDescId = 'invite-role-desc';

  // Refs for role buttons to support keyboard navigation
  const roleButtonRefs = useRef([]);

  const findNextEnabled = (startIndex, dir) => {
    const len = roleOptions.length;
    if (len === 0) return 0;
    // simple next index calculation (wraps with modulo). No disabled options currently.
    return (startIndex + dir + len) % len;
  };

  const handleRoleKeyDown = (e, idx) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = findNextEnabled(idx, 1);
      roleButtonRefs.current[next]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = findNextEnabled(idx, -1);
      roleButtonRefs.current[prev]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      const first = findNextEnabled(-1, 1);
      roleButtonRefs.current[first]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = findNextEnabled(0, -1);
      roleButtonRefs.current[last]?.focus();
    }
  };

  // --- Invitation statistics (used / active / expired / total)
  const totalInvites = invites.length;
  const usedInvites = invites.filter(inv => inv.used).length;
  const expiredInvites = invites.filter(inv => !inv.used && moment().isAfter(inv.expiration_time)).length;
  const activeInvites = Math.max(0, totalInvites - usedInvites - expiredInvites);

  // Percentages for the donut chart (fallback to 0 to avoid NaN)
  const usedPct = totalInvites ? Math.round((usedInvites / totalInvites) * 100) : 0;
  const activePct = totalInvites ? Math.round((activeInvites / totalInvites) * 100) : 0;
  const expiredPct = Math.max(0, 100 - usedPct - activePct);

  // Small sparkline: invites created per day over last 7 days
  // Calculate creation time as expiration_time - 3 days
  const lastNDays = 7;
  const countsByDay = Array.from({length: lastNDays}, (_, i) => {
    const day = moment().startOf('day').subtract(lastNDays - 1 - i, 'days');
    const count = invites.filter(inv => {
      // Calculate invite creation time: expiration_time - 3 days
      const createdTime = moment(inv.expiration_time).subtract(3, 'days');
      return createdTime.isBetween(day, moment(day).endOf('day'), null, '[]');
    }).length;
    return {day, count};
  });
  const maxCount = Math.max(1, ...countsByDay.map(d => d.count));
  const sparkPoints = countsByDay.map((d, i) => {
    const x = (i / (lastNDays - 1)) * 100;
    const y = 100 - (d.count / maxCount) * 100; // invert for SVG y coords
    return `${x},${y}`;
  }).join(' ');


  const [email, setEmail] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // filter by status (all/active/used/expired) and sort by created date
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' newest first, 'asc' oldest first
  const [filteredInvites, setFilteredInvites] = useState([]);

  // Pagination state: default to 12 per page
  const [page, setPage] = useState(1);
  const perPageOptions = [6, 12, 24, 48];
  const [perPage, setPerPage] = useState(perPageOptions[0]);

  useEffect(() => {
    dispatch(fetchInvites());
  }, [dispatch]);

  // Fetch resources when role is not forecaster
  useEffect(() => {
    if (role.toLowerCase() !== 'forecaster') {
      dispatch(fetchManagedResources());
    } else {
      // Clear selected resources when role is forecaster
      setSelectedResources([]);
    }
  }, [role, dispatch]);

  useEffect(() => {
    if (errorMessage) toast.error(errorMessage);
    if (successMessage) toast.success(successMessage);
  }, [errorMessage, successMessage]);

  useEffect(() => {
    if (errorMessage || successMessage) {
      const timer = setTimeout(() => dispatch(clearUserMessages()), 2000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage, dispatch]);

  // filter and reset page on search / status / sort order
  useEffect(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    let filtered = invites.slice();

    // text search
    if (q) {
      filtered = filtered.filter(inv => (inv.email || '').toLowerCase().includes(q));
    }

    // status filter
    if (statusFilter && statusFilter !== 'all') {
      if (statusFilter === 'used') {
        filtered = filtered.filter(inv => !!inv.used);
      } else if (statusFilter === 'expired') {
        filtered = filtered.filter(inv => !inv.used && moment().isAfter(inv.expiration_time));
      } else if (statusFilter === 'active') {
        filtered = filtered.filter(inv => !inv.used && !moment().isAfter(inv.expiration_time));
      }
    }

    // order by created date
    filtered.sort((a, b) => {
      const aDate = moment(a.created_at || a.created).valueOf();
      const bDate = moment(b.created_at || b.created).valueOf();
      return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
    });

    setFilteredInvites(filtered);
    setPage(1);
  }, [invites, searchTerm, statusFilter, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredInvites.length / perPage));
  const paginated = filteredInvites.slice((page - 1) * perPage, page * perPage);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent read-only market owners from submitting invites
    if (isMarketOwnerReadOnly) {
      dispatch(clearUserMessages());
      dispatch({type: 'user/sendInvite/rejected', payload: 'You do not have permission to send invites.'});
      return;
    }
    if (!email) {
      dispatch(clearUserMessages());
      dispatch({type: 'user/sendInvite/rejected', payload: 'Please enter a valid email address.'});
      return;
    }
    try {
      // include the selected role and resources when sending the invite
      const inviteData = {email, role};
      // Only include resources if role is not forecaster
      if (role.toLowerCase() !== 'forecaster') {
        inviteData.resources = selectedResources.length > 0 ? selectedResources : [];
      } else {
        // Trial accounts only make sense for forecasters
        inviteData.is_trial = isTrial;
      }
      await dispatch(sendInvite(inviteData)).unwrap();
      setEmail('');
      setRole('Forecaster');
      setIsTrial(true);
      setSelectedResources([]);
      await dispatch(fetchInvites());
    } catch (err) {
      // handled in slice
    }
  };

  const copyToken = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      toast.info('Token copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy token.');
    }
  };

  // Small row-card component inline — one per row, improved visuals
  const InviteCard = ({invite}) => {
    const isExpired = moment().isAfter(invite.expiration_time);

    // find friendly label for role (fallback to capitalized value or 'Forecaster')
    const roleLabel = (roleOptions.find(r => r.value === (invite.role || 'forecaster')) || {label: (invite.role ? String(invite.role).replace(/_/g, ' ') : 'Forecaster')}).label;

    return (
      <div className="w-full rounded-lg bg-surface dark:bg-gray-870 p-3 sm:p-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          {/* Main content area */}
          <div className="min-w-0 flex-1 space-y-2 sm:space-y-1">
            {/* Email and Status Badge - Stack on mobile, inline on desktop */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {blurSensitive ? <span className="blur-sm select-none">{invite.email}</span> : invite.email}
                </div>
                {/* Role badge - inline on all sizes */}
                <div className="flex-shrink-0 inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">
                  {roleLabel}
                </div>
                {invite.is_trial && (
                  <div className="flex-shrink-0 inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 dark:bg-amber-900 dark:text-amber-100">
                    Trial
                  </div>
                )}
              </div>

              {/* Status badge */}
              <div className="flex-shrink-0">
                {invite.used ? (
                  <div className="inline-flex items-center gap-1.5 h-7 leading-none text-xs sm:text-sm text-green-800 bg-green-50 px-2.5 sm:px-3 rounded-md dark:bg-green-800 dark:text-green-100">
                    <CheckCircleIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4"/> Used
                  </div>
                ) : isExpired ? (
                  <div className="inline-flex items-center gap-1.5 h-7 leading-none text-xs sm:text-sm text-red-800 bg-red-50 px-2.5 sm:px-3 rounded-md dark:bg-red-800 dark:text-red-100">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4"/> Expired
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1.5 h-7 leading-none text-xs sm:text-sm text-brand-800 bg-brand-50 px-2.5 sm:px-3 rounded-md dark:bg-brand-800 dark:text-brand-100">
                    Active
                  </div>
                )}
              </div>
            </div>

            {/* Token row - hide on mobile, show on desktop */}
            <div className="hidden sm:flex items-center gap-2">
              <div className="font-mono text-xs text-gray-500 dark:text-gray-400 truncate" title={invite.token}>
                {invite.token.substring(0, 8)}...
              </div>
              <button
                type="button"
                onClick={() => copyToken(invite.token)}
                aria-label="Copy token"
                className="inline-flex items-center justify-center h-5 w-5 text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 focus:outline-none"
              >
                <ClipboardIcon className="h-4 w-4"/>
              </button>
            </div>

            {/* Bottom info row */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-1.5">
                <span className="font-medium">Expires:</span>
                <span>
                  {moment(invite.expiration_time).format('D MMM YYYY, h:mm A')}{' '}
                  {getLocalTimezoneLabel(invite.expiration_time)}
                </span>
              </div>
              {invite.inviter_email && (
                <div className="flex items-center gap-1.5">
                  <span className="hidden sm:inline text-gray-400 dark:text-gray-500">·</span>
                  <span className="italic">
                    invited by {blurSensitive ? <span className="blur-sm select-none">{invite.inviter_email}</span> : invite.inviter_email}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Invite card */}
        <div
          className="col-span-1 rounded-xl bg-surface dark:bg-gray-870 shadow-sm border border-gray-200/70 dark:border-gray-600/70 divide-y divide-gray-100 dark:divide-gray-700">
          <div className="p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Send Invitation</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Enter an email address to send an
                invitation.</p>
            </div>

            <form onSubmit={handleSubmit} className={`space-y-3 ${isMarketOwnerReadOnly ? 'opacity-80' : ''}`}>
              <fieldset disabled={isMarketOwnerReadOnly} aria-disabled={isMarketOwnerReadOnly} className="space-y-3">
                <div>
                  {/*<label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>*/}
                  <div className="relative mt-0">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
                    </div>
                    <FormInput
                      id="invite-email"
                      name="invite-email"
                      type="email"
                      leftIcon="email"
                      required
                      placeholder="Enter user's email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 w-full"
                      disabled={isMarketOwnerReadOnly}
                    />
                  </div>
                </div>

                {/* Role segmented toggle (a little bigger) */}
                <div className="mt-3">
                  <div className="text-xs text-gray-600 dark:text-gray-300">Select the role</div>
                  <div className="mt-2 inline-flex rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-900 p-2 gap-1" role="tablist"
                       aria-label="Role selector">
                    {roleOptions.map((opt, i) => {
                      const active = opt.value === role;
                      const disabled = isMarketOwnerReadOnly;
                      return (
                        <button
                          key={opt.value}
                          ref={(el) => {
                            roleButtonRefs.current[i] = el;
                          }}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          aria-controls={roleDescId}
                          aria-disabled={disabled}
                          disabled={disabled}
                          onClick={() => !disabled && setRole(opt.value)}
                          onKeyDown={(e) => handleRoleKeyDown(e, i)}
                          className={`px-3 py-2 text-xs rounded-md focus:outline-none transition-colors duration-150 ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${active ? 'bg-brand-600 text-white font-semibold shadow-sm' : 'text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/50'}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Very small helper text showing the selected role description */}
                  <p id={roleDescId} className="mt-1 text-[11px] text-gray-500 dark:text-gray-400" role="status"
                     aria-live="polite">{roleDescriptions[role]}</p>

                </div>

                {/* Trial flag - only for forecaster invites */}
                {role.toLowerCase() === 'forecaster' && (
                  <div className="mt-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isTrial}
                        onChange={(e) => setIsTrial(e.target.checked)}
                        disabled={isMarketOwnerReadOnly}
                        className="rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500"
                      />
                      <span>Trial account</span>
                    </label>
                    <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      Trial forecasters can only access the playground until promoted to production.
                    </p>
                  </div>
                )}

                {/* Resource selection - only show for non-forecaster roles */}
                {role.toLowerCase() !== 'forecaster' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Assign Resources (Optional)
                    </label>
                    {loadingResources ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400 py-2">Loading resources...</div>
                    ) : availableResources.length > 0 ? (
                      <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200/70 dark:border-gray-600/70 rounded-md p-3">
                        {availableResources.map((resource) => (
                          <label
                            key={resource.id}
                            className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 p-1.5 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedResources.includes(resource.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedResources([...selectedResources, resource.id]);
                                } else {
                                  setSelectedResources(selectedResources.filter(id => id !== resource.id));
                                }
                              }}
                              disabled={isMarketOwnerReadOnly}
                              className="rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500"
                            />
                            <span>{resource.name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 dark:text-gray-400 py-2">
                        No resources available to assign.
                      </div>
                    )}
                    {selectedResources.length > 0 && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {selectedResources.length} resource(s) selected
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <CustomButton
                    type="submit"
                    icon="envelope"
                    caption={loading ? 'Sending...' : 'Send Invite'}
                    disabled={loading || isMarketOwnerReadOnly}
                    size="mediumSend"
                    solid={true}
                    className={loading || isMarketOwnerReadOnly ? 'opacity-60 cursor-wait' : ''}
                  />
                  <button type="button" onClick={() => setEmail('')}
                          className="px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm"
                          disabled={isMarketOwnerReadOnly}
                          aria-disabled={isMarketOwnerReadOnly}
                  >Clear
                  </button>
                </div>

                {isMarketOwnerReadOnly ? (
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">You have read-only market owner access
                    and cannot send invites.</div>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400">Invites expire automatically — tokens are
                    single-use.
                  </div>
                )}
              </fieldset>
            </form>
          </div>

          {/* Stats section - separate div for full-width divider */}
          <div className="p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Stats</h3>
            {/* Invitations summary + small visuals (donut + sparkline) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              {/* Donut (CSS conic-gradient) */}
              <div className="flex items-center gap-3">
                <div className="relative inline-block" style={{width: 64, height: 64}}>
                  <div
                    aria-hidden
                    className="rounded-full"
                    style={{
                      width: 64,
                      height: 64,
                      backgroundImage: `conic-gradient(#10b981 ${usedPct}%, #6366f1 ${usedPct}% ${usedPct + activePct}%, #ef4444 ${usedPct + activePct}% 100%)`,
                    }}
                  />
                  {/* inner circle to create donut hole */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="rounded-full bg-surface dark:bg-gray-870" style={{width: 38, height: 38}}>
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">{totalInvites}</div>
                        <div className="text-[10px] text-gray-500 dark:text-gray-400">Total</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div className="text-sm">
                  <div className="flex items-center gap-2"><span
                    className="inline-block w-2 h-2 rounded-full bg-green-500"/> <span
                    className="text-xs text-gray-700 dark:text-gray-200">Used {usedInvites} ({usedPct}%)</span></div>
                  <div className="flex items-center gap-2 mt-1"><span
                    className="inline-block w-2 h-2 rounded-full bg-brand-500"/> <span
                    className="text-xs text-gray-700 dark:text-gray-200">Active {activeInvites} ({activePct}%)</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1"><span
                    className="inline-block w-2 h-2 rounded-full bg-red-500"/> <span
                    className="text-xs text-gray-700 dark:text-gray-200">Expired {expiredInvites} ({expiredPct}%)</span>
                  </div>
                </div>
              </div>

              {/* Sparkline: invites over last 7 days */}
              <div className="sm:ml-2 overflow-hidden">
                {/* smaller caption + sparkline so it fits the card */}
                <div className="text-[9px] text-gray-500 dark:text-gray-400 mb-1">Invites (last {lastNDays} days)</div>
                <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-8">
                  <polyline fill="none" stroke="#60a5fa" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"
                            points={sparkPoints}/>
                  <polyline fill="none" stroke="#93c5fd" strokeWidth="3" strokeLinecap="round" opacity="0.12"
                            points={sparkPoints}/>
                </svg>
                <div className="mt-1 text-[9px] text-gray-500 dark:text-gray-400 flex flex-wrap gap-2">
                  {countsByDay.map((d, i) => (
                    <span key={i}>{d.day.format('dd')}:{d.count}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: List panel */}
        <div
          className="col-span-1 lg:col-span-2 rounded-xl bg-surface dark:bg-gray-870 p-6 shadow-sm border border-gray-200/70 dark:border-gray-600/70 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Sent Invitations</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Recent invites and their status.</p>
          </div>

          {/* Filters Row - Stack on mobile, row on desktop */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 sm:justify-end">

            {/* Search - full width on mobile */}
            <div className="relative w-full sm:w-80">
              <FormInput
                type="text"
                leftIcon="search"
                size="medium"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by email..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-surface dark:bg-gray-870 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            {/* Status and Order - side by side on mobile */}
            <div className="flex gap-3">
              <div className="flex flex-col flex-1">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="used">Used</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              <div className="flex flex-col flex-1">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="desc">Newest first</option>
                  <option value="asc">Oldest first</option>
                </select>
              </div>
            </div>

          </div>

          <div>
            {paginated.length > 0 ? (
              <div className="flex flex-col gap-4">
                {paginated.map(inv => (
                  <InviteCard key={inv.id || inv.token} invite={inv}/>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">No invites found.</div>
            )}
          </div>

          {/* Pagination */}
          <div>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={filteredInvites.length}
              itemsPerPage={perPage}
              onPageChange={setPage}
              onItemsPerPageChange={(newPerPage) => {
                setPerPage(newPerPage);
                setPage(1);
              }}
              itemsPerPageOptions={perPageOptions}
            />
          </div>

        </div>
      </div>
    </div>
  );
}
